import React from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { UploadItem } from '../../stores/uploadTrayStore';
import { formatFileSize } from './formatFileSize';

interface UploadTrayItemProps {
  item: UploadItem;
  onRetry: (localId: string) => void;
  onRemove: (localId: string) => void;
  onResolveCollision: (localId: string, action: 'keep-both' | 'skip') => void;
}

function formatSpeed(bytesPerSec: number): string {
  if (bytesPerSec >= 1_048_576) {
    return `${(bytesPerSec / 1_048_576).toFixed(1)} MB/s`;
  }
  return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
}

export const UploadTrayItem: React.FC<UploadTrayItemProps> = ({
  item,
  onRetry,
  onRemove,
  onResolveCollision,
}) => {
  const isCollision = item.status === 'collision';
  const isInProgress =
    item.status === 'uploading' ||
    item.status === 'requesting-url' ||
    item.status === 'confirming';
  const isFailed = item.status === 'failed';
  const isCompleted = item.status === 'completed';

  return (
    <Box
      data-testid="upload-tray-item"
      sx={{
        px: 2,
        py: 1,
        '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.fileName}
        </Typography>

        {isCompleted && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(item.file.size)}
            </Typography>
            <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
          </Box>
        )}

        {isFailed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              data-testid="upload-tray-item-retry"
              size="small"
              onClick={() => onRetry(item.localId)}
            >
              <ReplayIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              data-testid="upload-tray-item-remove"
              size="small"
              onClick={() => onRemove(item.localId)}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {isInProgress && (
        <Box sx={{ mt: 0.5 }}>
          <LinearProgress
            variant={
              item.status === 'uploading' ? 'determinate' : 'indeterminate'
            }
            value={item.progress}
            sx={{ height: 4, borderRadius: 2 }}
          />
          {item.status === 'uploading' && item.speed > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 0.25,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {formatSpeed(item.speed)}
              </Typography>
              {item.eta > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ~{item.eta}s remaining
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {isFailed && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 14 }} />
          <Typography variant="caption" color="error">
            {item.error || 'Upload failed'}
          </Typography>
        </Box>
      )}

      {isCollision && (
        <Box sx={{ mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon color="warning" sx={{ fontSize: 14 }} />
            <Typography variant="caption" color="text.secondary">
              A file with this name already exists
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onResolveCollision(item.localId, 'keep-both')}
              data-testid="collision-keep-both"
            >
              Keep both
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onResolveCollision(item.localId, 'skip')}
              data-testid="collision-skip"
            >
              Skip
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
